import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Instagram, Search } from "lucide-react";
import { useSettings, CATEGORIES } from "../context/SettingsContext";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";

const NAV = [
  { label: "HOME", to: "/" },
  { label: "SHOP ALL", to: "/shop" },
  { label: "NEW DROP", to: "/shop?tag=new-drop" },
  { label: "BEST SELLER", to: "/shop?tag=best-seller" },
  { label: "SALE", to: "/sale", red: true },
];

export default function Header() {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-neutral-200" : "bg-white border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-2 md:w-1/3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-1" aria-label="menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] p-0">
              <div className="flex h-16 items-center justify-between border-b px-5">
                <span className="font-display text-xl tracking-[0.3em]">{settings.brandName}</span>
              </div>
              <nav className="flex flex-col px-5 py-6">
                {NAV.map((n) => (
                  <Link
                    key={n.label}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    className={`py-3 text-sm font-medium tracking-widest border-b border-neutral-100 ${n.red ? "text-red-600" : "text-neutral-800"}`}
                  >
                    {n.label}
                  </Link>
                ))}
                <p className="mt-6 mb-2 text-xs tracking-widest text-neutral-400">SHOP BY CATEGORY</p>
                {CATEGORIES.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/shop/${c.slug}`}
                    onClick={() => setOpen(false)}
                    className="py-2.5 text-sm tracking-widest text-neutral-600"
                  >
                    {c.name}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <nav className="hidden md:flex items-center gap-7">
            {NAV.slice(1).map((n) => (
              <Link
                key={n.label}
                to={n.to}
                className={`text-xs font-medium tracking-[0.2em] transition-colors ${n.red ? "text-red-600 hover:text-red-700" : "text-neutral-700 hover:text-black"}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
        </div>

        <Link
          to="/"
          className="md:w-1/3 text-center font-display text-2xl md:text-[28px] font-medium tracking-[0.45em] pl-3"
        >
          {settings.brandName}
        </Link>

        <div className="flex items-center justify-end gap-4 md:w-1/3">
          <button
            onClick={() => navigate("/shop")}
            className="hidden md:inline-flex text-neutral-700 hover:text-black"
            aria-label="search"
          >
            <Search className="h-[18px] w-[18px]" />
          </button>
          <a
            href={settings.instagram}
            target="_blank"
            rel="noreferrer"
            className="text-neutral-700 hover:text-black"
            aria-label="instagram"
          >
            <Instagram className="h-[18px] w-[18px]" />
          </a>
        </div>
      </div>
    </header>
  );
}
